import { useRecoilState } from "recoil";
import { authAtom, AuthState } from "../recoil/auth/atom";
import ky, { Options } from "ky";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions extends Options {
  headers: Record<string, string>;
}

type RequestFunction = <T = any>(
  url: string,
  body?: any,
  overrideUrl?: boolean,
  formData?: boolean
) => Promise<T>;

interface RequestMethods {
  get: RequestFunction;
  post: RequestFunction;
  put: RequestFunction;
  patch: RequestFunction;
  _delete: RequestFunction;
}

export const useRequest = (): RequestMethods => {
  const [auth, setAuth] = useRecoilState(authAtom);

  const request = (method: HttpMethod) => {
    return async <T = any>(
      url: string,
      body?: any,
      overrideUrl = false,
      formData = false
    ): Promise<T> => {
      const requestOptions: RequestOptions = {
        method,
        headers: authHeader(),
        hooks: {
          afterResponse: [
            async (request, _options, response) => {
              if (response.status === 401) {
                if (!auth?.id) {
                  localStorage.removeItem("user");
                  setAuth(null);
                  return;
                }

                try {
                  const user = await ky
                    .post(
                      `${process.env.REACT_APP_API_URL}authorization/refresh-token?&id=${auth.id}`
                    )
                    .json<AuthState>();
                  
                  if (user.isAuthenticated) {
                    setAuth(user);
                    request.headers.set("Authorization", `Bearer ${user.token}`);
                    return ky(request);
                  } else {
                    localStorage.removeItem("user");
                    setAuth(null);
                  }
                } catch (error) {
                  localStorage.removeItem("user");
                  setAuth(null);
                }
              }
            },
          ],
        },
      };

      if (body) {
        if (!formData) {
          requestOptions.headers["Content-Type"] = "application/json";
          requestOptions.body = JSON.stringify(body);
        } else {
          requestOptions.body = body;
        }
      }

      return _fetch<T>(url, requestOptions, overrideUrl);
    };
  };

  const authHeader = (): Record<string, string> => {
    const token = auth?.token;
    const isLoggedIn = !!token;
    
    if (isLoggedIn) {
      return { Authorization: `Bearer ${token}` };
    } else {
      return {};
    }
  };

  const _fetch = async <T = any>(
    url: string,
    requestOptions: RequestOptions,
    overrideUrl: boolean
  ): Promise<T> => {
    const _url = overrideUrl ? url : `${process.env.REACT_APP_API_URL}${url}`;
    return ky(_url, requestOptions).json<T>();
  };

  return {
    get: request("GET"),
    post: request("POST"),
    patch: request("PATCH"),
    put: request("PUT"),
    _delete: request("DELETE"),
  };
};